import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import {
  Save as SaveIcon,
  NavigateNext as NextIcon,
  Place as SiteIcon,
} from "@mui/icons-material";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import { apiCall } from '../../../services/ApiCall';
import config from '../../../config/config.json';
import SiteContact from './EditSiteContact';

const EditSite = ({ onSave, setShowDetails, editData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [currentPage, setCurrentPage] = useState("editCompany");
  const [siteData, setSiteData] = useState({});
  const [siteUserData, setSiteUserData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    id: siteData?.id || '',
    araId: siteData?.araId || '',
    company: siteData?.company || '',
    companyId: siteData?.companyId || '',
    recheckCycle: siteData?.recheckCycle || '',
    address: siteData?.address || '',
    address2: siteData?.address2 || '',
    city: siteData?.city || '',
    state: siteData?.state || '',
    zipcode: siteData?.zipcode || '',
    recheckCycle: siteData?.recheckCycle || '',
  });

  const primaryColor = "#0d6efd";

  // Original API functions (unchanged logic)
  const getCompanyList = async () => {
    const { isSuccess, data } = await apiCall(`${config.POST_LOGIN_API_URL}getCompanyList`, {}, {}, "GET");
    if (isSuccess) {
      setCompanyData(data?.data?.reverse());
      const companyOptions = data?.data?.reverse().map((company) => ({
        value: company.id,
        label: company.name
      }));
      setCompanyOptions(companyOptions);
    }
  };

  const getSitesContact = async () => {
    // Add null check for editData
    if (!editData || !editData.id) {
      console.error("No edit data provided");
      return;
    }

    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesContact`,
      {},
      { siteId: editData.id },
      "GET"
    );
    if (isSuccess) {
      setSiteUserData(data?.data?.sitesUserData || []);
      setSiteData(data?.data?.sitesData || {});
    } else {
      console.log("error");
    }
  };

  // Original event handlers (unchanged logic)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSiteData({
      ...siteData,
      [name]: value,
    });
  };

  const changeCompany = (event, selectedOption) => {
    setSiteData((prevAnswer) => ({
      ...prevAnswer,
      companyId: selectedOption ? Number(selectedOption.value) : '',
    }));
    setSelectedCompany(selectedOption);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleNextClick = () => {
    setCurrentPage("mainContact");
  };

  const handlePrevious = () => {
    setCurrentPage("editCompany");
  };

  // Original address selection logic (unchanged)
  const handleSelect = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      const addressComponents = results[0].address_components;
      const getAddressComponent = (types) => {
        const component = addressComponents.find((comp) =>
          types.some((type) => comp.types.includes(type))
        );
        return component ? component.long_name : "";
      };

      const street =
        getAddressComponent(["street_number"]) +
        " " +
        getAddressComponent(["route"]);
      const city = getAddressComponent(["locality"]);
      const state = getAddressComponent(["administrative_area_level_1"]);
      const zipcode = getAddressComponent(["postal_code"]);

      setSiteData((prevAnswer) => ({
        ...prevAnswer,
        address: street,
        city,
        state,
        zipcode,
      }));
    } catch (error) {
      console.error("Error fetching address details: ", error);
    }
  };

  const handleSave = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}updateSitesData`,
      {},
      { siteId: siteData.id, ...siteData },
      "POST"
    );
    if (isSuccess) {
      setShowDetails(false);
    }
  };

  // Original useEffect hooks (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getCompanyList();
      getSitesContact();
    }
  }, []);

  useEffect(() => {
    setSelectedCompany(companyOptions.find(option => Number(option.value) == siteData.companyId));
  }, [companyOptions, siteData.companyId]);

  // Original conditional rendering (unchanged logic)
  if (currentPage === "mainContact") {
    return (
      <SiteContact
        onPrevious={handlePrevious}
        siteData={siteData}
        siteUserData={siteUserData}
        setShowDetails={setShowDetails}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      {/* Site Details Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          bgcolor: primaryColor,
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <SiteIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Site Details
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{
          bgcolor: '#f8f9fa',
          p: { xs: 2, md: 3 }
        }}>
          <form onSubmit={handleSubmit}>
            {/* First Row - ARA ID and Company */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ARA ID"
                  name="araId"
                  value={siteData.araId || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: primaryColor, fontWeight: 500 }}>
                    Company
                  </Typography>
                  {!selectedCompany && (
                    <Alert severity="info" sx={{ mb: 1, fontSize: '0.75rem' }}>
                      No matching company found
                    </Alert>
                  )}
                  <Autocomplete
                    value={selectedCompany}
                    onChange={changeCompany}
                    options={companyOptions}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selectedCompany ? "Select a company" : "Add a company"}
                        size={isSmall ? "small" : "medium"}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: primaryColor + '40',
                            },
                            '&:hover fieldset': {
                              borderColor: primaryColor,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: primaryColor,
                            },
                          },
                        }}
                      />
                    )}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Second Row - Address Fields */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1, 
                      color: primaryColor,
                      fontWeight: 500,
                      fontSize: isSmall ? '0.75rem' : '0.875rem'
                    }}
                  >
                    Address
                  </Typography>
                  <PlacesAutocomplete
                    value={siteData.address || ''}
                    onChange={(value) =>
                      setSiteData({ ...siteData, address: value })
                    }
                    onSelect={handleSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <Box sx={{ position: 'relative' }}>
                        <TextField
                          {...getInputProps({ placeholder: "Search Street" })}
                          fullWidth
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                        {(loading || suggestions.length > 0) && (
                          <Paper
                            elevation={3}
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {loading && (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2">Loading...</Typography>
                              </Box>
                            )}
                            {suggestions.map((suggestion, index) => (
                              <Box
                                key={index}
                                {...getSuggestionItemProps(suggestion)}
                                sx={{
                                  p: 2,
                                  cursor: 'pointer',
                                  backgroundColor: suggestion.active ? primaryColor + '10' : 'white',
                                  '&:hover': {
                                    backgroundColor: primaryColor + '20',
                                  },
                                  borderBottom: '1px solid #eee',
                                }}
                              >
                                <Typography variant="body2">
                                  {suggestion.description}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    )}
                  </PlacesAutocomplete>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address 2"
                  name="address2"
                  value={siteData.address2 || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Third Row - City, State, Zipcode, Recheck Cycle */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={siteData.city || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={siteData.state || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Zipcode"
                  name="zipcode"
                  value={siteData.zipcode || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Recheck Cycle"
                  name="recheckCycle"
                  value={siteData.recheckCycle || ''}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  '&:hover': {
                    backgroundColor: `${primaryColor}10`,
                    borderColor: primaryColor,
                  },
                  minWidth: 120,
                }}
              >
                Save
              </Button>
              
              <Button
                type="button"
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNextClick}
                sx={{
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9,
                  },
                  minWidth: 120,
                }}
              >
                Next
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditSite;